import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import StoreMallDirectoryIcon from '@material-ui/icons/StoreMallDirectory';
import { makeStyles } from '@material-ui/styles';

const STORAGE_LOCATIONS = [
  { name: 'ROSA', code: 'ROSA' },
  { name: 'Romania Hub', code: '2515' }
];

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: 12,
    overflow: 'hidden'
  },
  title: {
    padding: theme.spacing(2.5, 3),
    background: 'linear-gradient(135deg, #173447 0%, #24566f 100%)',
    color: theme.palette.common.white
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center'
  },
  titleIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    marginRight: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.14)'
  },
  titleText: {
    color: theme.palette.common.white,
    fontWeight: 600
  },
  subtitleText: {
    color: 'rgba(255, 255, 255, 0.78)'
  },
  content: {
    padding: theme.spacing(3)
  },
  tableContainer: {
    border: '1px solid #dfe6ea',
    borderRadius: 10,
    boxShadow: '0 4px 14px rgba(23, 52, 71, 0.08)',
    overflow: 'hidden'
  },
  tableHeader: {
    backgroundColor: '#f4f7f9'
  },
  headerCell: {
    color: '#526572',
    fontWeight: 700
  },
  row: {
    cursor: 'pointer',
    transition: 'background-color 150ms ease'
  },
  selectedRow: {
    cursor: 'pointer',
    backgroundColor: '#e9f3f7 !important',
    boxShadow: 'inset 4px 0 0 #24566f'
  },
  locationName: {
    color: '#173447',
    fontWeight: 600
  },
  code: {
    display: 'inline-block',
    minWidth: 58,
    padding: theme.spacing(0.5, 1),
    borderRadius: 6,
    backgroundColor: '#eef2f4',
    color: '#314957',
    fontFamily: 'monospace',
    fontWeight: 700,
    textAlign: 'center'
  },
  actions: {
    padding: theme.spacing(2, 3),
    borderTop: '1px solid #e7ecef'
  },
  confirmButton: {
    minWidth: 110,
    backgroundColor: '#173447',
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: '#24566f'
    }
  }
}));

const RomaniaStorageLocationDialog = ({
  open,
  selectedCode,
  onSelect,
  onClose
}) => {
  const { t } = useTranslation('overview');
  const classes = useStyles();
  const [draftCode, setDraftCode] = useState(selectedCode);

  useEffect(() => {
    if (open) {
      setDraftCode(selectedCode || '');
    }
  }, [open, selectedCode]);

  const handleConfirm = () => {
    onSelect(draftCode);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography className={classes.title}>
        <Box className={classes.titleContent}>
          <Box className={classes.titleIcon}>
            <StoreMallDirectoryIcon />
          </Box>
          <Box>
            <Typography variant="h6" className={classes.titleText}>
              {t('SelectSapStorageLocation', {
                defaultValue: 'Choose storage location'
              })}
            </Typography>
            <Typography variant="body2" className={classes.subtitleText}>
              {t('SapStorageLocationDescription', {
                defaultValue: 'Select where the material order will be fulfilled.'
              })}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small">
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell />
                <TableCell className={classes.headerCell}>
                  {t('StorageLocationName', { defaultValue: 'Location' })}
                </TableCell>
                <TableCell className={classes.headerCell}>
                  {t('StorageLocationCode', { defaultValue: 'Code' })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {STORAGE_LOCATIONS.map(location => {
                const isSelected = draftCode === location.code;
                return (
                  <TableRow
                    key={location.code}
                    hover
                    selected={isSelected}
                    onClick={() => setDraftCode(location.code)}
                    className={isSelected ? classes.selectedRow : classes.row}
                  >
                    <TableCell padding="checkbox">
                      <Radio
                        checked={isSelected}
                        onChange={() => setDraftCode(location.code)}
                        color="primary"
                        inputProps={{ 'aria-label': location.name }}
                      />
                    </TableCell>
                    <TableCell className={classes.locationName}>
                      {location.name}
                    </TableCell>
                    <TableCell>
                      <span className={classes.code}>{location.code}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} color="default">
          {t('Cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!draftCode}
          className={classes.confirmButton}
        >
          {t('Confirm', { defaultValue: 'Confirm' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

RomaniaStorageLocationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  selectedCode: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

RomaniaStorageLocationDialog.defaultProps = {
  selectedCode: ''
};

export default RomaniaStorageLocationDialog;
